import { create } from 'zustand'
import axios from 'axios'
import type { Card, CardType, CardContent } from '../types'
import { CARD_MIN_SIZES, GRID_COLS, GRID_ROWS } from '../types'

export interface Subpage {
  id: number
  slug: string
  title: string
  sort_order: number
}

interface CardState {
  cards: Card[]
  isLoading: boolean
  isPublicTransitioning: boolean
  editMode: boolean
  selectedCardId: number | null
  currentPageId: number | null  // null = main page
  subpages: Subpage[]

  setEditMode: (mode: boolean) => void
  setIsPublicTransitioning: (val: boolean) => void
  setSelectedCard: (id: number | null) => void
  setCurrentPageId: (id: number | null) => void

  fetchCards: (pageId?: number | null) => Promise<void>
  fetchSubpages: () => Promise<void>
  addCard: (cardType: CardType) => Promise<void>
  updateCard: (id: number, updates: Partial<Card>) => Promise<void>
  updateCardPosition: (id: number, gridX: number, gridY: number) => void
  updateCardSize: (id: number, width: number, height: number) => void
  updateCardRect: (id: number, gridX: number, gridY: number, width: number, height: number) => void
  deleteCard: (id: number) => Promise<void>
  saveCardPositions: () => Promise<void>

  checkOverlap: (cardId: number, x: number, y: number, width: number, height: number) => boolean
}

export const useCardStore = create<CardState>((set, get) => ({
  cards: [],
  isLoading: false,
  isPublicTransitioning: false,
  editMode: true,
  selectedCardId: null,
  currentPageId: null,
  subpages: [],

  setEditMode: (mode) => set({ editMode: mode }),
  setIsPublicTransitioning: (val) => set({ isPublicTransitioning: val }),
  setSelectedCard: (id) => set({ selectedCardId: id }),
  setCurrentPageId: (id) => set({ currentPageId: id }),

  fetchCards: async (pageId) => {
    set({ isLoading: true })
    try {
      const pid = pageId !== undefined ? pageId : get().currentPageId
      const params = pid !== null && pid !== undefined ? `?page_id=${pid}` : '?page_id=null'
      const response = await axios.get(`/api/cards${params}`)
      set({ cards: response.data.cards, isLoading: false })
    } catch (error) {
      console.error('Failed to fetch cards:', error)
      set({ isLoading: false })
    }
  },

  fetchSubpages: async () => {
    try {
      const response = await axios.get('/api/subpages')
      set({ subpages: response.data.subpages })
    } catch (error) {
      console.error('Failed to fetch subpages:', error)
    }
  },

  addCard: async (cardType) => {
    try {
      const { checkOverlap } = get()
      const { cards, currentPageId } = get()

      if (cards.length >= 30) {
        throw new Error('Page card limit reached (30)')
      }

      if (cardType === 'profile' || cardType === 'spotify_top' || cardType === 'spotify_current') {
        if (cards.some((c) => c.card_type === cardType)) {
          throw new Error(`Only one ${cardType} card is allowed per page`)
        }
      }

      const minSize = CARD_MIN_SIZES[cardType]

      // Place new cards at the highest available empty area (topmost y, then x)
      let gridX = 0
      let gridY = 0
      let found = false
      for (let y = 0; y <= GRID_ROWS; y++) {
        for (let x = 0; x <= GRID_COLS - minSize.width; x++) {
          if (!checkOverlap(-1, x, y, minSize.width, minSize.height)) {
            gridX = x
            gridY = y
            found = true
            break
          }
        }
        if (found) break
      }

      const response = await axios.post('/api/cards', {
        card_type: cardType,
        page_id: currentPageId,
        grid_x: gridX,
        grid_y: gridY,
        content: getDefaultContent(cardType)
      })
      set((state) => ({
        cards: [...state.cards, response.data.card],
        selectedCardId: response.data.card?.id ?? state.selectedCardId,
      }))
    } catch (error) {
      console.error('Failed to add card:', error)
      throw error
    }
  },

  updateCard: async (id, updates) => {
    try {
      const response = await axios.put(`/api/cards/${id}`, updates)
      set((state) => ({
        cards: state.cards.map((card) =>
          card.id === id ? response.data.card : card
        )
      }))
    } catch (error) {
      console.error('Failed to update card:', error)
      throw error
    }
  },

  updateCardPosition: (id, gridX, gridY) => {
    const { cards, checkOverlap } = get()
    const card = cards.find((c) => c.id === id)
    if (!card) return

    const clampedX = Math.max(0, Math.min(gridX, GRID_COLS - card.grid_width))
    const clampedY = Math.max(0, gridY)

    if (checkOverlap(id, clampedX, clampedY, card.grid_width, card.grid_height)) {
      return
    }

    set((state) => ({
      cards: state.cards.map((c) =>
        c.id === id ? { ...c, grid_x: clampedX, grid_y: clampedY } : c
      )
    }))
  },

  updateCardSize: (id, width, height) => {
    const { cards, checkOverlap } = get()
    const card = cards.find((c) => c.id === id)
    if (!card) return

    const clampedWidth = Math.min(width, GRID_COLS - card.grid_x)

    if (checkOverlap(id, card.grid_x, card.grid_y, clampedWidth, height)) {
      return
    }

    set((state) => ({
      cards: state.cards.map((c) =>
        c.id === id ? { ...c, grid_width: clampedWidth, grid_height: height } : c
      )
    }))
  },

  updateCardRect: (id, gridX, gridY, width, height) => {
    const { cards, checkOverlap } = get()
    const card = cards.find((c) => c.id === id)
    if (!card) return

    const clampedY = Math.max(0, gridY)
    const clampedHeight = Math.max(1, height)

    const safeWidth = Math.max(1, width)
    const clampedX = Math.max(0, Math.min(gridX, GRID_COLS - safeWidth))
    const clampedWidth = Math.max(1, Math.min(safeWidth, GRID_COLS - clampedX))

    if (checkOverlap(id, clampedX, clampedY, clampedWidth, clampedHeight)) {
      return
    }

    set((state) => ({
      cards: state.cards.map((c) =>
        c.id === id
          ? {
            ...c,
            grid_x: clampedX,
            grid_y: clampedY,
            grid_width: clampedWidth,
            grid_height: clampedHeight,
          }
          : c
      )
    }))
  },

  deleteCard: async (id) => {
    try {
      await axios.delete(`/api/cards/${id}`)
      set((state) => ({
        cards: state.cards.filter((card) => card.id !== id)
      }))
    } catch (error) {
      console.error('Failed to delete card:', error)
      throw error
    }
  },

  saveCardPositions: async () => {
    const { cards } = get()
    try {
      await axios.put('/api/cards/batch', {
        cards: cards.map((c) => ({
          id: c.id,
          grid_x: c.grid_x,
          grid_y: c.grid_y,
          grid_width: c.grid_width,
          grid_height: c.grid_height
        }))
      })
    } catch (error) {
      console.error('Failed to save card positions:', error)
    }
  },

  checkOverlap: (cardId, x, y, width, height) => {
    const { cards } = get()
    return cards.some((card) => {
      if (card.id === cardId) return false

      const cardRight = card.grid_x + card.grid_width
      const cardBottom = card.grid_y + card.grid_height
      const newRight = x + width
      const newBottom = y + height

      return !(x >= cardRight || newRight <= card.grid_x || y >= cardBottom || newBottom <= card.grid_y)
    })
  }
}))

function getDefaultContent(cardType: CardType): CardContent {
  switch (cardType) {
    case 'profile':
      return {
        name: 'Your Name',
        nickname: '(nickname)',
        socialLinks: [],
        fontSize: 'medium',
        profileVAlign: 'top',
      }
    case 'link':
      return { url: '', label: 'Link', linkAlign: 'right' }
    case 'text':
      return { text: 'Add your text here...', textFontSize: 'small', textAlign: 'left', textVAlign: 'top' }
    case 'photo':
      return { imageUrl: '', aspectRatio: '1:1', photoLinkUrl: '', photoZoomDisabled: false }
    case 'audio':
      return { audioUrl: '', audioKey: '' }
    case 'spotify_top':
      return { spotifyTimeRange: 'short_term', songCount: 3 }
    case 'spotify_current':
      return {}
    case 'video':
      return { videoUrl: '', videoType: 'youtube' }
    case 'social_icon':
      return { socialIconType: 'instagram', socialIconUrl: '' }
    default:
      return {}
  }
}
